import React, { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import ProjectsPanel from "@/components/admin/ProjectsPanel";
import CategoriesPanel from "@/components/admin/CategoriesPanel";
import ContentPanel from "@/components/admin/ContentPanel";
import TeamPanel from "@/components/admin/TeamPanel";
import EnquiriesPanel from "@/components/admin/EnquiriesPanel";
import { Stack, FolderSimple, Tag, TextT, UsersThree, SignOut, ArrowSquareOut, Tray } from "@phosphor-icons/react";

const NAV = [
  { key: "projects", label: "Projects", icon: FolderSimple },
  { key: "categories", label: "Categories", icon: Tag },
  { key: "content", label: "Content", icon: TextT },
  { key: "enquiries", label: "Enquiries", icon: Tray },
  { key: "team", label: "Team", icon: UsersThree },
];

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("projects");
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    api.get("/admin/contacts/unread_count").then(({ data }) => setUnread(data.count || 0)).catch(() => {});
  }, [user, tab]);

  if (loading) return <div className="min-h-screen bg-background" />;
  if (!user) return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="w-64 shrink-0 border-r border-border bg-card min-h-screen sticky top-0 h-screen flex flex-col">
        <div className="px-6 py-7 border-b border-border">
          <p className="font-serif text-xl tracking-[0.12em]">NAT HABITAT</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">Studio CMS</p>
        </div>
        <nav className="flex-1 py-6 px-3 space-y-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <button
                key={n.key}
                data-testid={`nav-${n.key}`}
                onClick={() => setTab(n.key)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                  tab === n.key ? "bg-secondary text-foreground" : "text-foreground/60 hover:text-foreground hover:bg-secondary/50"
                }`}
              >
                <Icon size={18} /> {n.label}
                {n.key === "enquiries" && unread > 0 && (
                  <span data-testid="enquiries-unread-badge" className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-accent text-accent-foreground text-[11px] font-medium grid place-items-center">
                    {unread}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border space-y-1">
          <a href="/" target="_blank" rel="noopener noreferrer" className="w-full flex items-center gap-3 px-4 py-3 text-sm text-foreground/60 hover:text-foreground">
            <ArrowSquareOut size={18} /> View Site
          </a>
          <div className="px-4 py-2 text-xs text-muted-foreground">{user.name} · {user.role}</div>
          <button onClick={() => { logout(); navigate("/admin"); }} data-testid="logout-btn" className="w-full flex items-center gap-3 px-4 py-3 text-sm text-destructive hover:bg-secondary/50">
            <SignOut size={18} /> Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 px-8 md:px-12 py-10 md:py-14 overflow-x-hidden">
        {tab === "projects" && <ProjectsPanel />}
        {tab === "categories" && <CategoriesPanel />}
        {tab === "content" && <ContentPanel />}
        {tab === "enquiries" && <EnquiriesPanel />}
        {tab === "team" && <TeamPanel />}
      </main>
    </div>
  );
}
